/**
 * Customer Activity Chart
 *
 * Shows daily new vs returning customers
 */

import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatLocalizedDate } from "../lib/format-localized-date";
import { LockedChartCard } from "./locked-chart-card";
import { MetricTooltip } from "./metric-tooltip";

interface CustomerActivityChartProps {
  data: Array<{
    date: string;
    newCustomers: number;
    returningCustomers: number;
  }> | null;
}

export function CustomerActivityChart({ data }: CustomerActivityChartProps) {
  const { t, i18n } = useTranslation("reports");

  // Format date for X-axis display
  const formatXAxisDate = (dateStr: string) => {
    const dt = DateTime.fromISO(dateStr);
    if (!dt.isValid) {
      return dateStr;
    }
    return formatLocalizedDate(dt, "MMM d", i18n.language);
  };

  // Format date for tooltip
  const formatTooltipDate = (dateStr: string) => {
    const dt = DateTime.fromISO(dateStr);
    if (!dt.isValid) {
      const jsDate = new Date(dateStr);
      if (!Number.isNaN(jsDate.getTime())) {
        return formatLocalizedDate(DateTime.fromJSDate(jsDate), "MMM d, yyyy", i18n.language);
      }
      return dateStr;
    }
    return formatLocalizedDate(dt, "MMM d, yyyy", i18n.language);
  };

  // Generate mock data for locked state
  const mockData = Array.from({ length: 10 }, (_, i) => ({
    date: `Day ${i + 1}`,
    newCustomers: Math.floor(Math.random() * 20) + 10,
    returningCustomers: Math.floor(Math.random() * 30) + 20,
  }));

  // If data is null (basic tier), show locked version
  if (data === null) {
    const mockContent = (
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={mockData} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis
            dataKey="date"
            className="text-xs"
            tick={{ fill: "currentColor" }}
            angle={-45}
            textAnchor="end"
            height={80}
          />
          <YAxis className="text-xs" tick={{ fill: "currentColor" }} />
          <Legend
            wrapperStyle={{ paddingTop: "20px" }}
            iconType="circle"
            formatter={(value) => <span className="text-xs">{value}</span>}
          />
          <Line
            type="monotone"
            dataKey="newCustomers"
            stroke="#3b82f6"
            strokeWidth={2}
            dot={{ fill: "#3b82f6", r: 3 }}
            name="New Customers"
          />
          <Line
            type="monotone"
            dataKey="returningCustomers"
            stroke="#10b981"
            strokeWidth={2}
            dot={{ fill: "#10b981", r: 3 }}
            name="Returning Customers"
          />
        </LineChart>
      </ResponsiveContainer>
    );

    return (
      <LockedChartCard
        title={t("charts.customerActivity.title")}
        tooltip={t("charts.customerActivity.tooltip")}
        mockContent={mockContent}
        height="h-full"
      />
    );
  }

  const hasData = data && data.length > 0;

  return (
    <Card className="animate-in fade-in slide-in-from-bottom-4 duration-500 h-full">
      <CardHeader>
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center">
          {t("charts.customerActivity.title")}
          <MetricTooltip content={t("charts.customerActivity.tooltip")} />
        </CardTitle>
      </CardHeader>
      <CardContent className={hasData ? "p-0" : ""}>
        {!hasData ? (
          <div className="flex items-center justify-center h-[300px]">
            <p className="text-sm text-muted-foreground">No data</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis
                dataKey="date"
                className="text-xs"
                tick={{ fill: "currentColor" }}
                tickFormatter={formatXAxisDate}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis className="text-xs" tick={{ fill: "currentColor" }} />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    return (
                      <div className="bg-popover border rounded-md px-3 py-2 shadow-md">
                        <p className="text-xs font-medium mb-1">
                          {formatTooltipDate(payload[0].payload.date)}
                        </p>
                        <div className="space-y-1">
                          <p className="text-xs">
                            <span className="text-blue-500">●</span> New: {payload[0].value}
                          </p>
                          <p className="text-xs">
                            <span className="text-green-500">●</span> Returning:{" "}
                            {payload[1]?.value || 0}
                          </p>
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Legend
                wrapperStyle={{ paddingTop: "20px" }}
                iconType="circle"
                formatter={(value) => <span className="text-xs">{value}</span>}
              />
              <Line
                type="monotone"
                dataKey="newCustomers"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={{ fill: "#3b82f6", r: 3 }}
                name="New Customers"
                isAnimationActive={true}
                animationDuration={1000}
                animationEasing="ease-out"
              />
              <Line
                type="monotone"
                dataKey="returningCustomers"
                stroke="#10b981"
                strokeWidth={2}
                dot={{ fill: "#10b981", r: 3 }}
                name="Returning Customers"
                isAnimationActive={true}
                animationDuration={1000}
                animationEasing="ease-out"
                animationBegin={200}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
